/** Server state for the /calls surface. Nothing here touches the media plane. */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { CallHistoryEntry, CallSettings, Voicemail } from '@cyphex/types/calls';
import type { UserRef } from '@cyphex/types';
import { toast } from '@cyphex/ui';
import { callsApi, type CallHistoryFilter } from '../data/callsApi';
import { CALL_KEYS } from '../data/queryKeys';

export function useCallHistory(filter: CallHistoryFilter): UseQueryResult<CallHistoryEntry[]> {
  return useQuery({
    queryKey: CALL_KEYS.history(filter),
    queryFn: () => callsApi.history(filter).then((page) => page.calls),
  });
}

export function useVoicemail(enabled: boolean): UseQueryResult<Voicemail[]> {
  return useQuery({
    queryKey: CALL_KEYS.voicemail(),
    queryFn: () => callsApi.voicemail().then((page) => page.voicemails),
    enabled,
  });
}

export function useCallSettings(enabled: boolean): UseQueryResult<CallSettings> {
  return useQuery({
    queryKey: CALL_KEYS.settings(),
    queryFn: () => callsApi.settings(),
    enabled,
  });
}

export function useSaveCallSettings(): (patch: Partial<CallSettings>) => void {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (patch: Partial<CallSettings>) => callsApi.saveSettings(patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CALL_KEYS.settings() });
      toast.success('Call settings saved.');
    },
    onError: () => toast.error('Could not save. Try again.'),
  });
  return mutation.mutate;
}

/**
 * Playing a message marks it read, deleting it removes it from the inbox.
 *
 * The list has always rendered unread messages in bold and read ones in
 * regular weight; with nothing able to set `is_read` that styling was
 * decoration. These two are what make it mean something.
 */
export function useVoicemailActions(): {
  markRead: (voicemailId: string) => void;
  remove: (voicemailId: string) => void;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: CALL_KEYS.voicemail() });
    void queryClient.invalidateQueries({ queryKey: ['calls', 'history'] });
  };

  const read = useMutation({
    mutationFn: (voicemailId: string) => callsApi.markVoicemailRead(voicemailId, true),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (voicemailId: string) => callsApi.deleteVoicemail(voicemailId),
    onSuccess: () => {
      invalidate();
      toast.success('Voicemail deleted.');
    },
    onError: () => toast.error('Could not delete that message.'),
  });

  return { markRead: read.mutate, remove: remove.mutate };
}

/** Directory lookup for click-to-call. Blank query returns nothing, not everyone. */
export function usePeopleSearch(query: string): UseQueryResult<UserRef[]> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: CALL_KEYS.people(trimmed),
    queryFn: () => callsApi.searchPeople(trimmed).then((page) => page.users),
    enabled: trimmed.length > 1,
  });
}
